import { ContentObject } from '../../base';
import { createScores } from '../../base/scoreHelper';

export const uberQueryGPTCaseStudy: ContentObject = {
    id: 'uber-query-gpt',
    kind: 'system',
    type: 'case-study',
    name: 'GenAI Generates SQL Queries & Streamlines Data Access at Uber',
    description:
        "Uber's QueryGPT uses a multi-agent RAG system to convert natural language to SQL, reducing query writing time from ~10 mins to ~3 mins despite schema complexity and token limits.",
    url: 'https://www.uber.com/en-IN/blog/query-gpt/',
    scores: createScores({
        detailedMetrics: 8.0,
        relevanceToUseCase: 6.0,
        reliabilityOfSource: 7.0,
        strategicAndTacticalInformation: 9.0,
        technicalImplementationDetails: 9.0,
        noVendorBias: 9.0,
        relevanceToClient: 8.0,
    }),
    publishedAt: new Date(Date.now() - 13 * 24 * 60 * 60 * 1000), // 13 days ago
    markdown: `# 1. Who the Source of Information Is
 
 The case is derived from a Blog Post from Uber's engineer team, titled 'Query GPT'
 
 # 2. Successes
 
 Uber's QueryGPT is a transformative tool that converts natural language queries into executable SQL queries. This innovation has dramatically shortened the time needed for complex query formulation—from approximately 10 minutes to just 3 minutes per query. The system not only democratizes data access for non-expert users (including engineers, operations managers, and data scientists) but also serves as an efficiency booster for technical teams that otherwise would spend significant hours manually crafting queries.
 
 Relevant Quote:
 > "…writing the queries takes a decent amount of time—each query could take around 10 minutes—but with QueryGPT, employees can get reliable queries in just about 3 minutes… a major productivity gain." 
 
 # 3. Challenges
 
 The implementation of QueryGPT presented several technical and operational challenges:
 
 ## Complex Schema Handling: 
 Because Uber deals with enormous data sets, some database tables include up to 200 columns. Directly using these massive schemas in prompts risks exceeding token limits with even state-of-the-art LLMs. 
 Relevant Quote: 
 > "…we have some really large schemas on some tier one tables at Uber with some spanning over 200 columns… using three or more of these tables would break the token limit." 
 
 ## Token Limit Constraints: 
 Even using models such as GPT-4 Turbo with up to 128k token capacity, the combined size of multiple large schemas in one prompt can overwhelm the system. 
 Relevant Quote: 
 > "…the largest available model at that time only supported 32,000 tokens… some requests using large schemas would break the LLM call." 
 
 ## Determining User Intent: 
 Translating a natural language request into the precise database schema needed was nontrivial. The original simple vector search for relevant samples did not sufficiently capture the user's intent. 
 Relevant Quote: 
 > "…directly understanding from the English language question to which tables to use is a tough problem… we wanted our system to understand the user intent." 
 
 ## Hallucinations in LLMs: 
 Due to the probabilistic nature of LLM responses, on occasion the system produced queries referencing tables or columns that did not exist. 
 Relevant Quote: 
 > "…one issue that is very common with LLMs… we do see instances where the LLM would generate a query with tables that don't exist… hallucinations remain an area where we are constantly working." 
 
 # 4. Technical Details
 
 QueryGPT leverages a multi-step retrieval augmented generation (RAG) process that integrates various sub-agents to break down the complex problem of converting natural language into accurate SQL queries. The process involves:
 
 ## Vector Search and RAG:
 - An initial vector search is performed on a library of SQL samples and schema definitions to identify the most relevant tables and sample queries based on the user's English language query.
 - This retrieval augmentation supports the LLM with private company data, improving context.
  
 Relevant Quote:
 > "QueryGPT uses large language models, vector databases, and similarity search to generate complex queries from English questions." 
 
 ## Introduction of Specialized Agents:
 - The "intent agent" first classifies the user's query for mapping it to one or more specific workspaces (curated clusters of relevant tables). 
 - The "table agent" further refines the selection by allowing for user feedback on which tables best represent the query's needs. 
 - The "column prune agent" automatically reduces a table's columns to only those relevant to the query, helping avoid token overflows.
 
 Relevant Quote:
 > "…they introduced workspaces—a curated collection of SQL samples and tables—tailored to specific business domains. With the intent agent, table agent, and column prune agent, we reduce the retrieval scope and ensure that only the most relevant schema information is used." 
 
 ## Iterative Approach and Evaluation:
 - QueryGPT underwent more than 20 iterations during internal "generative AI hack days" at Uber, with each cycle improving task-specific accuracy. 
 - A multi-phase evaluation process was adopted to assess the effectiveness of each intermediate agent (intent, table, column pruning), benchmarking accuracy against a set of "golden" SQL queries.
 
 Relevant Quote:
 > "…we evaluated the performance using standardized techniques—you check if the intent agent correctly identifies the workspace, whether the table agent picks the right tables and the final SQL matches with a golden SQL sample." 
 
 # 5. Results
 
 The improvements delivered by QueryGPT are significant: The conversion of query drafting time from 10 minutes to 3 minutes represents a considerable productivity boost for teams. This efficiency has been further underlined by internal evaluations, which indicate that a substantial portion of users appreciate the improvements. For instance, a limited release to operations and support teams shows promising uptake.
 
 Relevant Quote:
 > "With our limited release to some teams in operations and support, we are averaging about 300 daily active users with about 78% saying that the SQL queries have reduced the amount of time they would spend authoring them." 
 
 # 6. Vendors Used
 
 While the implementation appears to be largely internal—with Uber engineering and data teams leading the effort—it leverages several external tools and industry-standard approaches:
  
 ## Large Language Models:
 - Although not explicitly named, Uber's early versions used an LLM that was later replaced/upgraded (with improvements like token length support coming via models such as OpenAI's GPT-4 Turbo).
 
 ## Vector Databases and Embeddings:
 - The system uses vector-based similarity search to identify relevant SQL samples and schema information. While the vendor for these vector databases isn't directly named, the approach aligns with commonly used platforms in the industry.
 
 ## Prompt Engineering and Language Chain Tools:
 - The refined prompts (including custom internal language instructions) and the holistic RAG design indicate the use of advanced prompt engineering practices and may incorporate open-source libraries similar to LangChain for conversational retrieval.
 
 Relevant Quote:
 > "The first version used a fairly simple RAG—passing the user's prompt along with three relevant tables and seven SQL samples to the LLM… and later integrating a more sophisticated set of agents to fine-tune the process." 
 
 # Conclusion
 
 Uber's QueryGPT is a textbook example of how generative AI can significantly boost developer and analyst productivity by automating a traditionally time-consuming task (i.e., writing detailed SQL queries). Its layered architecture—featuring intent classification, table selection, and column pruning—ensures that even complex and massive schemas are handled efficiently. Despite technical challenges like token limitations and inherent LLM hallucinations, iterative improvements and a robust evaluation approach have resulted in a tool that accelerates data access and query generation, ultimately making technical roles more efficient and freeing up valuable engineer time.
 
 This case study, presented by DataTrek, illustrates a concrete, production-ready implementation where generative AI is transforming workflow efficiency, demonstrating both the promise and the practical challenges of integrating AI into a complex enterprise environment.`,
    references: [
        {
            id: '1',
            name: 'QueryGPT: Generate SQL Queries with Natural Language',
            author: 'Uber Engineering',
            year: '2024', // Assuming 2024 based on context
            publisher: 'Uber Engineering Blog',
            url: 'https://www.uber.com/en-IN/blog/query-gpt/',
        },
    ],
};
